import { useCallback, useEffect } from 'react';
import * as Yup from 'yup';
import { useField } from 'formik';
import { createUseStyles, useTheme } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  Feature,
  ModeratedTaskFormat,
  ModeratedTaskSetting,
  Session,
  SessionFormat,
  StudyType,
  useAccountById,
  useAccountCalendars,
  useCalendar,
  useCapabilities,
  useIsAdmin,
  useMe,
  VideoCallProvider,
} from '@app/api';
import {
  Input,
  InputLabel,
  NumberInput,
  TextArea,
  YupValidators,
} from '@app/form';
import {
  Button,
  useParticipantDisplayName,
  InformationBox,
  LockFeatureChip,
} from '@app/core';

import {
  CalendarSelect,
  isVideoCallProviderAvailableForCalendar,
  OrganizationMemberSelect,
  PhoneNumberInput,
  Toggle,
  useUnlockFeature,
  VideoConferencingProviderSelect,
} from 'components';

import { SessionFormSection } from './SessionFormSection';
import { AttendeesSelect } from 'components/Calendar/AttendeesSelect';
import { ZoomAccountSelect } from 'components/Calendar/ZoomAccountSelect';
import { DisconnectedCalendarWarn } from './DisconnectedCalendarWarn';
import { AddCalendarAccountButton } from 'pages/Dashboard/Settings/components/AddCalendarAccountButton';

interface Props {
  session: Session;
  name: string;
}

export const getModeratedTaskSettingValidationSchema = (
  format?: SessionFormat,
) =>
  Yup.object().shape({
    moderatorAccountId: Yup.string().uuid().required(),
    moderatorCalendarId: Yup.string().optional().nullable(),
    videoCallLink: Yup.string().url().optional().nullable(),
    videoCallDetails:
      format === SessionFormat.VIDEO_CALL
        ? Yup.string().required()
        : Yup.mixed().nullable(),
    videoCallProvider:
      format === SessionFormat.VIDEO_CALL
        ? Yup.string().oneOf(Object.values(VideoCallProvider)).required()
        : Yup.mixed().nullable(),
    address:
      format === SessionFormat.PHYSICAL_MEETING
        ? Yup.string().required()
        : Yup.mixed().nullable(),
    addressDetails:
      format === SessionFormat.PHYSICAL_MEETING
        ? Yup.string().required()
        : Yup.mixed().nullable(),
    visiblePhoneNumber: Yup.string().when('phoneVisibilityConsent', {
      is: true,
      then: (schema) => YupValidators.isPhoneNumber(true, schema),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    attendees: Yup.array().of(
      Yup.object()
        .shape({
          id: Yup.string().uuid().optional().nullable(),
          email: Yup.string().email().required(),
          accountId: Yup.string().uuid().optional().nullable(),
          taskSettingId: Yup.string().uuid().optional().nullable(),
        })
        .optional()
        .nullable(),
    ),
    videoCallProviderId: Yup.string().when('videoCallProvider', {
      is: VideoCallProvider.ZOOM,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  });

export const getModeratedTaskSettingInitialValue = (
  taskSetting: ModeratedTaskSetting,
) => {
  const common = {
    moderatorAccountId: taskSetting.moderatorAccountId,
    moderatorCalendarId: taskSetting.moderatorCalendarId,
    format: taskSetting.format,
    phoneVisibilityConsent: taskSetting.phoneVisibilityConsent,
    visiblePhoneNumber: taskSetting.visiblePhoneNumber,
    groupMeeting: taskSetting.groupMeeting,
    numberOfParticipantByMeeting: taskSetting.numberOfParticipantByMeeting,
  };
  if (taskSetting.format === ModeratedTaskFormat.VIDEO_CALL) {
    return {
      ...common,
      videoCallLink: taskSetting.videoCallLink,
      videoCallDetails: taskSetting.videoCallDetails || '',
      videoCallProvider: taskSetting.videoCallProvider || '',
      videoCallProviderId: taskSetting.videoCallProviderId,
      attendees: taskSetting.attendees || [],
    };
  }
  if (taskSetting.format === ModeratedTaskFormat.PHYSICAL_MEETING) {
    return {
      ...common,
      address: taskSetting.address,
      addressDetails: taskSetting.addressDetails,
    };
  }
  return common;
};

const useStyles = createUseStyles((theme: Theme) => ({
  box: {
    marginTop: theme.marginBase,
  },
  planBox: {
    marginTop: theme.marginBase * 3,
  },
  upgradeBoxContent: {
    whiteSpace: 'pre-line',
  },
  boxButtonLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.marginBase,
  },
  toggle: {
    marginBottom: theme.marginBase,
  },
  videoCallLinkContainer: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  button: {
    minWidth: 'auto',
    whiteSpace: 'nowrap',
  },
  icon: {
    fontSize: theme.iconSize.regular,
  },
  list: {
    marginLeft: theme.marginBase * 4,
    marginTop: theme.marginBase,
  },
  subFieldContainer: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    marginTop: theme.marginBase,
  },
  phoneVisibleInput: {
    marginTop: 0,
  },
}));

export const ModeratedTaskSettingFields = ({ session, name }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();

  const capabilities = useCapabilities(session.planId, session.organizationId);
  const unlockCalendar = useUnlockFeature(Feature.CALENDAR_INTEGRATION);

  const { data: me } = useMe();
  const [{ value: taskSetting }, _, { setValue }] =
    useField<ModeratedTaskSetting>(name);
  const format = taskSetting?.format;
  const organizationId = session.organizationId;
  const { data: moderator } = useAccountById(taskSetting?.moderatorAccountId);

  const moderatorName = useParticipantDisplayName(moderator);

  const { data: calendars = [] } = useAccountCalendars(
    taskSetting?.moderatorAccountId,
  );
  const currentUserIsModerator = me?.id === taskSetting.moderatorAccountId;

  const onAccountChange = useCallback(() => {
    const { videoCallProvider, moderatorCalendarId, ...prevValue } =
      taskSetting;

    setValue({
      ...prevValue,
      moderatorCalendarId: undefined,
      videoCallProvider:
        videoCallProvider === VideoCallProvider.GOOGLE_MEET
          ? VideoCallProvider.GOOGLE_MEET
          : VideoCallProvider.CUSTOM,
    });
  }, [setValue, taskSetting]);

  const onProviderChange = useCallback(() => {
    const { videoCallLink, videoCallProviderId, ...prevValue } = taskSetting;
    setValue({
      ...prevValue,
      videoCallLink: undefined,
      videoCallProviderId: undefined,
    });
  }, [taskSetting, setValue]);

  const { data: selectedCalendar } = useCalendar(
    taskSetting?.moderatorCalendarId,
  );

  useEffect(() => {
    if (
      format !== ModeratedTaskFormat.VIDEO_CALL ||
      !selectedCalendar ||
      selectedCalendar.id !== taskSetting?.moderatorCalendarId ||
      isVideoCallProviderAvailableForCalendar(
        taskSetting.videoCallProvider,
        selectedCalendar,
      )
    ) {
      return;
    }

    const { videoCallLink, videoCallProviderId, ...prevValue } = taskSetting;
    setValue({
      ...prevValue,
      videoCallProvider: VideoCallProvider.CUSTOM,
      videoCallLink: undefined,
      videoCallProviderId: undefined,
    });
  }, [format, selectedCalendar, taskSetting, setValue]);

  if (session.studyType !== StudyType.MODERATED) {
    return null;
  }

  return (
    <SessionFormSection title={t('editSession.settings.taskSetting')}>
      {!capabilities?.calendarIntegration && (
        <InformationBox
          title={t(
            'editSession.settings.calendarIntegrationNotAvailable.title',
          )}
          variant="plan-upgrade"
          className={classes.planBox}
        >
          <p className={classes.upgradeBoxContent}>
            {t(
              'editSession.settings.calendarIntegrationNotAvailable.description',
            )}
          </p>
          <div className={classes.boxButtonLine}>
            <Button
              onClick={unlockCalendar}
              variant="muted"
              color="accent"
              text={t(
                'editSession.settings.calendarIntegrationNotAvailable.upgrade',
              )}
              small
            />
            <Button
              href={t(
                'editSession.settings.calendarIntegrationNotAvailable.learnMoreLink',
              )}
              target="_blank"
              variant="muted"
              color="accent"
              text={t(
                'editSession.settings.calendarIntegrationNotAvailable.learnMore',
              )}
              small
            />
          </div>
        </InformationBox>
      )}

      <OrganizationMemberSelect
        name={`${name}.moderatorAccountId`}
        selectionType="accountId"
        label={t('editSession.settings.moderator.name')}
        secondaryLabel={t('editSession.settings.moderator.explanation')}
        onChange={onAccountChange}
        organizationId={organizationId}
      />

      {(taskSetting.groupMeeting || isAdmin) && (
        <NumberInput
          name={`${name}.numberOfParticipantByMeeting`}
          label={t(`editSession.settings.numberOfParticipantByMeeting`, {
            context: taskSetting.groupMeeting ? 'group' : 'individual',
          })}
          max={session.numberOfParticipantRequired}
          min={1}
        />
      )}

      <div className={classes.subFieldContainer}>
        <InputLabel label={t('editSession.settings.moderator.calendarId')} />

        {calendars?.length ? (
          <>
            <CalendarSelect
              name={`${name}.moderatorCalendarId`}
              noMargin
              calendars={calendars}
              disabled={!capabilities?.calendarIntegration && !isAdmin}
            />
            <DisconnectedCalendarWarn
              calendars={calendars}
              name={`${name}.moderatorCalendarId`}
            />
          </>
        ) : (
          <InformationBox
            title={t('editSession.settings.moderator.noCalendarTitle', {
              context: currentUserIsModerator ? 'me' : 'other',
              moderator: moderatorName,
            })}
          >
            <Trans
              i18nKey="editSession.settings.moderator.noCalendarText"
              context={currentUserIsModerator ? 'me' : 'other'}
              components={{
                ul: <ul className={classes.list} />,
                li: <li />,
              }}
            />
            {currentUserIsModerator && (
              <div className={classes.boxButtonLine}>
                <LockFeatureChip
                  feature={Feature.CALENDAR_INTEGRATION}
                  planId={session.planId}
                />
                <AddCalendarAccountButton
                  children={t('editSession.settings.moderator.addCalendar')}
                />
              </div>
            )}
          </InformationBox>
        )}
      </div>

      {format === ModeratedTaskFormat.VIDEO_CALL && (
        <>
          {session.taskSettingId && (
            <AttendeesSelect
              organizationId={organizationId}
              taskSettingId={session.taskSettingId}
              planId={session.planId}
            />
          )}
          <VideoConferencingProviderSelect
            name={`${name}.videoCallProvider`}
            label={t('editSession.settings.videoCallProvider')}
            onChange={onProviderChange}
            noBottomMargin={!!taskSetting?.videoCallProvider}
            planId={session.planId}
            organizationId={session.organizationId}
            calendarId={taskSetting?.moderatorCalendarId}
          />
          {!!taskSetting?.videoCallProvider &&
            (taskSetting?.videoCallProvider ===
            VideoCallProvider.GOOGLE_MEET ? (
              <InformationBox
                variant="danger"
                title={t('editSession.settings.deprecatedMeet.title')}
                text={t('editSession.settings.deprecatedMeet.text')}
                className={classes.box}
              />
            ) : taskSetting?.videoCallProvider === VideoCallProvider.CUSTOM ? (
              <div className={classes.subFieldContainer}>
                <Input
                  name={`${name}.videoCallLink`}
                  label={t('sessions.display.videoCallLink')}
                  placeholder={t('sessions.placeholder.videoCallLink')}
                  noMargin
                  onBlur={(e: any) => {
                    const trimmed = e.target.value.trim();
                    if (trimmed !== e.target.value) {
                      setValue({
                        ...taskSetting,
                        videoCallLink: trimmed || null,
                      });
                    }
                  }}
                />
              </div>
            ) : taskSetting?.videoCallProvider === VideoCallProvider.ZOOM ? (
              <ZoomAccountSelect name={`${name}.videoCallProviderId`} />
            ) : (
              <InformationBox
                variant="info"
                text={t('editSession.settings.integratedBox')}
                className={classes.box}
              />
            ))}
          <TextArea
            secondaryLabel={t('editSession.settings.instructionsInfo')}
            name={`${name}.videoCallDetails`}
            label={t('sessions.display.videoCallDetails')}
            placeholder={t('sessions.placeholder.videoCallDetails')}
          />
        </>
      )}
      {format === ModeratedTaskFormat.PHYSICAL_MEETING && (
        <>
          <Input
            name={`${name}.address`}
            label={t('sessions.display.address')}
            placeholder={t('sessions.placeholder.address')}
          />
          <TextArea
            name={`${name}.addressDetails`}
            secondaryLabel={t('editSession.settings.instructionsInfo')}
            label={t('sessions.display.addressDetails')}
            placeholder={t('sessions.placeholder.addressDetails')}
          />
        </>
      )}
      <Toggle
        name={`${name}.phoneVisibilityConsent`}
        label={t('sessions.display.phoneVisibilityConsent.label')}
        containerClass={classes.toggle}
        explanation={t('sessions.display.phoneVisibilityConsent.explanation')}
        hideChildrenIfNotChecked
      >
        <div className={classes.subFieldContainer}>
          <PhoneNumberInput
            className={classes.phoneVisibleInput}
            name={`${name}.visiblePhoneNumber`}
            label={t('sessions.display.phoneVisibilityConsent.phoneLabel')}
            noMargin
          />
        </div>
      </Toggle>
    </SessionFormSection>
  );
};
